import { ReactNode } from "react";
import { sentinelTokens } from "@/app/providers/DesignSystemContext";
import { useTheme } from "@/app/providers/ThemeContext";

interface EnterpriseStatCardProps {
  label: string;
  value: string | number;
  accent?: "cyan" | "green" | "red" | "blue";
  icon?: ReactNode;
  trend?: {
    value: string;
    positive: boolean;
  };
  className?: string;
  animationDelay?: number;
}

export default function EnterpriseStatCard({
  label,
  value,
  accent = "cyan",
  icon,
  trend,
  className,
  animationDelay = 0,
}: EnterpriseStatCardProps) {
  const tokens = sentinelTokens;
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const accentColors = {
    cyan: tokens.colors.cyan,
    green: tokens.colors.green,
    red: tokens.colors.red,
    blue: tokens.colors.blue,
  };

  const accentColor = accentColors[accent];

  const cardBg = isDark ? tokens.colors.bg1 : "#ffffff";
  const cardBorder = isDark ? tokens.colors.border : "#c4c2bc";
  const cardBorderHover = isDark ? tokens.colors.borderBright : "#c5bbb0";

  return (
    <div
      className={`relative overflow-hidden transition-all duration-200 ${className ?? ""}`}
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        padding: "20px 22px",
        animation: tokens.animations.fadeIn,
        animationDelay: `${animationDelay}s`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = cardBorderHover;
        const before = e.currentTarget.querySelector(
          ".stat-card-glow"
        ) as HTMLElement;
        if (before) before.style.opacity = "1";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = cardBorder;
        const before = e.currentTarget.querySelector(
          ".stat-card-glow"
        ) as HTMLElement;
        if (before) before.style.opacity = "0";
      }}
    >
      {/* Top Glow Line */}
      <div
        className="stat-card-glow absolute top-0 left-0 right-0 h-[1px] transition-opacity duration-200"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          opacity: 0,
        }}
      />

      {/* Label */}
      <div
        style={{
          fontSize: "0.65rem",
          letterSpacing: "0.18em",
          color: isDark ? tokens.colors.textDim : "rgba(0,0,0,0.45)",
          textTransform: "uppercase",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {icon}
        {label}
      </div>

      {/* Value Row */}
      <div className="flex items-end justify-between gap-4">
        <div
          style={{
            fontFamily: tokens.fonts.display,
            fontSize: "2rem",
            fontWeight: 800,
            lineHeight: 1,
            color: isDark ? "#ffffff" : "#111111",
          }}
        >
          {value}
        </div>

        {trend && (
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: trend.positive ? tokens.colors.green : tokens.colors.red,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {trend.positive ? "↑" : "↓"} {trend.value}
          </div>
        )}
      </div>
    </div>
  );
}
